import { useEffect, useRef, useState } from "react";
import { CheckCircle } from "lucide-react";

const recentJobs = [
  { city: "Oro Valley", price: 350.00 },
  { city: "Tucson", price: 425.00 },
  { city: "Catalina", price: 375.00 },
  { city: "Sahuarita", price: 550.00 },
  { city: "SaddleBrooke", price: 400.00 },
  { city: "Oracle", price: 475.00 },
  { city: "Tucson", price: 625.00 },
  { city: "Oro Valley", price: 380.00 },
  { city: "Marana", price: 500.00 },
  { city: "Catalina", price: 450.00 },
  { city: "Sahuarita", price: 375.00 },
  { city: "SaddleBrooke", price: 525.00 },
  { city: "Tucson", price: 700.00 },
  { city: "Oracle", price: 395.00 },
  { city: "Oro Valley", price: 850.00 },
];

const ROW_HEIGHT = 56;
const VISIBLE_ROWS = 5;
const TOTAL_ITEMS = recentJobs.length;
const LIST_HEIGHT = ROW_HEIGHT * TOTAL_ITEMS;

const JobRow = ({ job, index }) => (
  <div
    className={`px-5 flex items-center justify-between border-b border-gray-800/60 ${
      index % 2 === 0 ? "bg-[#1A1A1A]" : "bg-[#1f1f1f]"
    }`}
    style={{ height: ROW_HEIGHT }}
    data-testid={`price-row-${index}`}
  >
    <div className="flex items-center gap-3">
      <div className="w-1.5 h-1.5 rounded-full bg-[#FF4D00]" />
      <div>
        <span className="font-body text-sm text-white font-medium block leading-tight">
          Stump Grinding
        </span>
        <span className="font-body text-xs text-gray-400">{job.city}</span>
      </div>
    </div>
    <span className="font-heading font-bold text-[#FF4D00] text-lg">
      ${job.price.toFixed(2)}
    </span>
  </div>
);

const RecentPricesSection = () => {
  const [paused, setPaused] = useState(false);
  const scrollRef = useRef(null);
  const offsetRef = useRef(0);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);

  const SPEED = 25; // pixels per second

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const tick = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      if (!paused) {
        offsetRef.current += (SPEED * delta) / 1000;
        if (offsetRef.current >= LIST_HEIGHT) {
          offsetRef.current -= LIST_HEIGHT;
        }
        el.style.transform = `translateY(-${offsetRef.current}px)`;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [paused]);

  return (
    <section className="py-16 lg:py-24 bg-white" id="pricing-widget">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Copy */}
          <div>
            <div className="accent-line mb-4" />
            <p className="font-heading text-sm font-bold text-[#FF4D00] uppercase tracking-wider mb-2">
              Honest, Transparent Pricing
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1A1A] uppercase mb-6 leading-tight">
              Real Prices from<br />Real Jobs
            </h2>
            <p className="font-body text-gray-600 leading-relaxed mb-6">
              We believe in <span className="font-semibold text-[#1A1A1A]">transparent pricing</span>. No hidden fees, no surprises. Here are actual prices from recent stump grinding jobs we've completed for your neighbors right here in Southern Arizona.
            </p>
            <div className="flex flex-wrap gap-4">
              {[
                { icon: CheckCircle, text: "No Hidden Fees" },
                { icon: CheckCircle, text: "Free Quotes" },
                { icon: CheckCircle, text: "Price Match" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <item.icon className="w-5 h-5 text-[#FF4D00]" />
                  <span className="font-body font-semibold text-sm text-[#1A1A1A]">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Auto-scrolling Ticker Widget */}
          <div
            className="bg-[#1A1A1A] border-2 border-[#FF4D00] overflow-hidden"
            data-testid="recent-prices-widget"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            {/* Widget Header */}
            <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="font-heading font-bold text-white uppercase text-base tracking-wide">
                Recent Jobs Completed
              </h3>
              <span className="inline-flex items-center gap-1.5 bg-[#FF4D00] px-3 py-1 text-white font-heading text-xs font-bold uppercase tracking-wider">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                Live Pricing
              </span>
            </div>

            {/* Column Headers */}
            <div className="px-5 py-2.5 border-b border-gray-800 flex items-center justify-between bg-[#222]">
              <span className="font-body text-xs font-semibold text-gray-400 uppercase tracking-wider">Service / Location</span>
              <span className="font-body text-xs font-semibold text-gray-400 uppercase tracking-wider">Price</span>
            </div>

            {/* Ticker viewport */}
            <div
              style={{ height: ROW_HEIGHT * VISIBLE_ROWS, overflow: "hidden" }}
              data-testid="prices-scroll-list"
            >
              <div ref={scrollRef} style={{ willChange: "transform" }}>
                {/* Render list twice for seamless loop */}
                {recentJobs.map((job, i) => (
                  <JobRow key={`a-${i}`} job={job} index={i} />
                ))}
                {recentJobs.map((job, i) => (
                  <JobRow key={`b-${i}`} job={job} index={i} />
                ))}
              </div>
            </div>

            {/* Widget Footer */}
            <div className="px-5 py-3 border-t border-gray-800 bg-[#222]">
              <p className="font-body text-xs text-gray-500 text-center">
                Prices vary based on stump size & accessibility. <span className="text-[#FF4D00] font-semibold">Get your free estimate today!</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecentPricesSection;
